import { Component, inject } from '@angular/core';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import {
  AgActionIconButtonComponent
} from '@shared/components/grid/ag-cells/common/ag-action-icon-button/ag-action-icon-button.component';
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { DocumentCommonService } from "@shared/services/documents-common.service";
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { DocumentRecord } from '@models/annual-statements/document-record';
import { SigningStatus } from '@models/annual-statements/annual-statement-enums';
import { AsicStatus } from "@models/documents/asic-status.enum";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { SubMenuItem } from '@models/ui-models/section-form-menu';

type ActionEvent = (data: unknown) => void;

type CellRendererParams = ICellRendererParams<DocumentRecord> & {
  openDocument: ActionEvent,
  openNotes: ActionEvent,
  deleteDocument: ActionEvent,
  openForm492: ActionEvent,
  openForm902: ActionEvent,
  downloadDocument: ActionEvent,
  markAsSigned: ActionEvent,
  markAsSent: ActionEvent,
  lodge: ActionEvent,
  markAsLodged: ActionEvent
};

@Component({
  selector: 'app-ag-document-actions',
  standalone: true,
  imports: [
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    AgActionIconButtonComponent,
    HasRoleDirective
  ],
  templateUrl: 'ag-document-actions.component.html',
  styleUrls: [
    '../../companies/ag-company-actions/ag-company-actions.component.scss',
    'ag-document-actions.component.scss'
  ]
})
export class AgDocumentActionsComponent implements ICellRendererAngularComp {
  #documentCommonService = inject(DocumentCommonService);

  readonly menuActions: SubMenuItem[] = [
    { title: 'Lodge', isVisable: () => this.islodgeVisible(), iconClass: 'icon ic-refresh-20 mr-1', action: () => this.lodge() },
    { title: 'Mark as Sent',  isVisable: () => this.isMarkAsSentVisible(), iconClass: 'icon ic-checkmark-circle-20 mr-1', action: () => this.markAsSent() },
    { title: 'Mark as Signed', isVisable: () => this.isMarkAsSignedVisible(), iconClass: 'icon ic-checkmark-circle-20 mr-1', action: () => this.markAsSigned() },
    { title: 'Mark as Lodged', isVisable: () => this.isMarkAsLodgedVisible(), iconClass: 'icon ic-checkmark-circle-20 mr-1', action: () => this.markAsLodged() },
    { title: 'Download', iconClass: 'icon ic-download-20 mr-1', action: () => this.downloadDocument() },
    { title: 'Request for Correction', isVisable: () => this.isCorrectionVisible(), iconClass: 'icon ic-refresh-20 mr-1', action: () => this.openForm492() },
    { title: 'Notification for Supplementary Information', isVisable: () => this.isCorrectionVisible(), iconClass: 'icon ic-download-20 mr-1', action: () => this.openForm902() },
    { title: 'Delete Document', isVisable: () => this.documentDeleteAvailable(), iconClass: 'icon ic-archive-20 mr-1', action: () => this.deleteDocument() },
  ];
  readonly AsicStatus = AsicStatus;

  params!: CellRendererParams;
  visibleActions: SubMenuItem[] = [];

  agInit(params: CellRendererParams): void {
    this.updateCell(params);
  }

  refresh(params: CellRendererParams): boolean {
    return this.updateCell(params);
  }

  updateCell(params: CellRendererParams): boolean {
    this.params = params;
    this.fillActions();

    return true;
  }

  fillActions(): void {
    this.visibleActions = this.menuActions.filter((action) => action.isVisable ? action.isVisable() : true);
  }

  openDocument(): void {
    this.params.openDocument(this.params.data);
  }

  openNotes(): void {
    this.params.openNotes(this.params.data);
  }

  deleteDocument(): void {
    this.params.deleteDocument(this.params.data);
  }

  openForm492(): void {
    this.params.openForm492(this.params.data);
  }

  openForm902(): void {
    this.params.openForm902(this.params.data);
  }

  lodge(): void {
    this.params.lodge(this.params.data);
  }

  markAsSigned(): void {
    this.params.markAsSigned(this.params.data);
  }

  markAsSent(): void {
    this.params.markAsSent(this.params.data);
  }

  markAsLodged(): void {
    this.params.markAsLodged(this.params.data);
  }

  downloadDocument(): void {
    this.params.downloadDocument(this.params.data);
  }

  islodgeVisible(): boolean {
    return (this.params.data?.status === DocumentStatusEnum.LodgementPending
        || this.params.data?.status === DocumentStatusEnum.SignaturePending)
      && this.params.data?.asicStatus === AsicStatus.NotLodged;
  }

  isMarkAsSignedVisible(): boolean {
    return [
        DocumentStatusEnum.SignaturePending,
        DocumentStatusEnum.LodgementPending
      ]
        .includes(this.params.data?.status!)
      && [
        SigningStatus.ReadyToSend,
        SigningStatus.Created,
        SigningStatus.Sent,
        SigningStatus.MarkedAsSent,
        SigningStatus.SentViaEmail,
      ]
        .includes(this.params.data?.signingStatus!);
  }

  isMarkAsSentVisible(): boolean {
     return this.params.data?.status === DocumentStatusEnum.SignaturePending
      && this.params.data?.signingStatus === SigningStatus.ReadyToSend || this.params.data?.signingStatus === SigningStatus.SentViaEmail;
  }

  isMarkAsLodgedVisible(): boolean {
    return this.params.data?.status === DocumentStatusEnum.LodgementPending
      && this.params.data?.asicStatus === AsicStatus.NotLodged;
  }

  isCorrectionVisible(): boolean {
    return this.params.data?.asicStatus === AsicStatus.Lodged
      && this.params.data?.type.includes('484');
  }

  documentDeleteAvailable(): boolean {
    return this.#documentCommonService.isAllowedToDeleteDocuments([this.params.data!]);
  }
}
